/**
 * Moto Location Hooks
 * ===================
 * React Query hooks for motorcycle location operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { motoLocationApi } from '@/services/api'
import type { MotoLocationCreate, MotoLocationUpdate } from '@/types'

// Query keys
export const motoLocationKeys = {
  all: ['motoLocations'] as const,
  lists: () => [...motoLocationKeys.all, 'list'] as const,
  list: () => [...motoLocationKeys.lists()] as const,
  details: () => [...motoLocationKeys.all, 'detail'] as const,
  detail: (id: number) => [...motoLocationKeys.details(), id] as const,
}

/**
 * Hook to fetch all moto locations.
 */
export function useMotoLocations() {
  return useQuery({
    queryKey: motoLocationKeys.list(),
    queryFn: () => motoLocationApi.list(),
  })
}

/**
 * Hook to fetch a single moto location.
 */
export function useMotoLocation(id: number) {
  return useQuery({
    queryKey: motoLocationKeys.detail(id),
    queryFn: () => motoLocationApi.get(id),
    enabled: !!id,
  })
}

/**
 * Hook to create a new moto location.
 */
export function useCreateMotoLocation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: MotoLocationCreate) => motoLocationApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: motoLocationKeys.lists() })
    },
  })
}

/**
 * Hook to update a moto location.
 */
export function useUpdateMotoLocation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: MotoLocationUpdate }) =>
      motoLocationApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: motoLocationKeys.lists() })
      queryClient.invalidateQueries({ queryKey: motoLocationKeys.detail(id) })
    },
  })
}

/**
 * Hook to delete a moto location.
 */
export function useDeleteMotoLocation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => motoLocationApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: motoLocationKeys.lists() })
    },
  })
}
